import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import { Client, clientApi } from '../services/clientApi';
import { DietProgram, dietProgramApi } from '../services/dietProgramApi';

interface UseDietitianDataOptions {
  fetchClients?: boolean;
  fetchAppointments?: boolean;
  fetchDietPrograms?: boolean;
}

export const useDietitianData = (options: UseDietitianDataOptions = {}) => {
  const {
    fetchClients = false,
    fetchAppointments = false,
    fetchDietPrograms = false,
  } = options;

  // Options'ı ref'te sakla ki her render'da yeni obje oluşturulmasın
  const optionsRef = useRef({
    fetchClients,
    fetchAppointments,
    fetchDietPrograms,
  });
  optionsRef.current = { fetchClients, fetchAppointments, fetchDietPrograms };

  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();
  const { dietitianId: contextDietitianId, userEmail: contextUserEmail, dietitian: contextDietitian, loadDietitian, clientsRefreshTrigger } = useAuth();
  const [dietitianId, setDietitianId] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [dietPrograms, setDietPrograms] = useState<DietProgram[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Role kontrolü: Çıkış yapmışsa sadece yönlendir; yanlış roldeyse hata göster
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const email = localStorage.getItem('userEmail');

    if (!email || !userRole) {
      navigate('/dietitian/login');
      return;
    }
    if (userRole !== 'dietitian') {
      showError(
        'Bu sayfaya erişim yetkiniz yok. Lütfen diyetisyen panelinden giriş yapın.',
        'Erişim Engellendi'
      );
      navigate('/dietitian/login');
      return;
    }

    setUserEmail(email);

    // Context'ten dietitian ID'yi al
    if (contextDietitianId) {
      setDietitianId(contextDietitianId);
    } else if (contextUserEmail === email && !contextDietitian) {
      // Context'te yoksa yükle
      loadDietitian(email).then(() => {
        // Context güncellendiğinde dietitianId de güncellenecek
      }).catch((error) => {
        console.error('Dietitian yükleme hatası:', error);
      });
    }
  }, [navigate, showError, contextDietitianId, contextUserEmail, contextDietitian, loadDietitian]);

  // Context'teki dietitianId değiştiğinde state'i güncelle
  useEffect(() => {
    if (contextDietitianId) {
      setDietitianId(contextDietitianId);
    }
  }, [contextDietitianId]);

  // Verileri çek
  useEffect(() => {
    const fetchData = async () => {
      if (!userEmail) {
        console.log('⚠️ userEmail henüz yüklenmedi, bekleniyor...');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Options'ı ref'ten al
        const { fetchClients, fetchAppointments, fetchDietPrograms } =
          optionsRef.current;

        console.log('📊 Veri çekiliyor, dietitian email:', userEmail);

        // Sadece gerektiğinde ek verileri çek
        const promises: Promise<any>[] = [];

        if (fetchClients) {
          promises.push(
            clientApi.getAllClients(userEmail).then((clientsData) => {
              console.log(`✅ ${clientsData.length} danışan yüklendi`);
              setClients(clientsData);
            })
          );
        }

        if (fetchAppointments && dietitianId) {
          promises.push(
            appointmentApi.getAppointmentsByDietitian(dietitianId).then((appointmentsData) => {
              console.log(`✅ ${appointmentsData.length} randevu yüklendi`);
              setAppointments(appointmentsData);
            })
          );
        }

        if (fetchDietPrograms && dietitianId) {
          promises.push(
            dietProgramApi.getDietProgramsByDietitian(dietitianId).then((dietProgramsData) => {
              console.log(`✅ ${dietProgramsData.length} program yüklendi`);
              setDietPrograms(dietProgramsData);
            })
          );
        }

        await Promise.all(promises);
      } catch (err) {
        setError('Veriler yüklenirken hata oluştu');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userEmail, dietitianId, clientsRefreshTrigger]);

  const refreshClients = async () => {
    try {
      const clientsData = await clientApi.getAllClients(userEmail);
      setClients(clientsData);
    } catch (err) {
      console.error('Error refreshing clients:', err);
    }
  };

  const refreshAppointments = async () => {
    try {
      if (!dietitianId) return;
      const appointmentsData = await appointmentApi.getAppointmentsByDietitian(dietitianId);
      setAppointments(appointmentsData);
    } catch (err) {
      console.error('Error refreshing appointments:', err);
    }
  };

  const refreshDietPrograms = async () => {
    try {
      if (!dietitianId) return;
      const dietProgramsData = await dietProgramApi.getDietProgramsByDietitian(dietitianId);
      setDietPrograms(dietProgramsData);
    } catch (err) {
      console.error('Error refreshing diet programs:', err);
    }
  };

  return {
    dietitianId,
    userEmail,
    clients,
    appointments,
    dietPrograms,
    loading,
    error,
    setClients,
    setAppointments,
    setDietPrograms,
    refreshClients,
    refreshAppointments,
    refreshDietPrograms,
    showSuccess,
    showError,
    showWarning,
    dietitian: contextDietitian,
  };
};
